import type { PromoCandidate } from '@supply-mover-2-0/shared';

/**
 * Promo widget selection — pure helpers used by the storefront
 * homepage widgets (banner / one-up / two-up / carousel / grid) to
 * pick which promos to show given a config.
 *
 * Two selection modes per widget config:
 *   - `auto` (default): pre-filter by displayLocations (when set),
 *     then rank candidates by `rankBy`, then take the top `limit`.
 *     The backend has already filtered candidates to the ones
 *     eligible for the portal/visitor (active window, targeting,
 *     usage gates, displayOnDashboard=true) so the helper just
 *     filters, sorts, and slices.
 *   - `manual`: admin pinned a specific list of promo IDs. We honour
 *     order, but still filter against the eligible set so an expired
 *     pinned promo doesn't show stale.
 *
 * Defaults per widget type (see PHASE-5 design docs and
 * dashboard-widgets-v2.md):
 *   - banner / one-up / two-up: rankBy='priority'
 *   - carousel: rankBy='priority', rotate (UI concern, not selection)
 *   - grid: rankBy='priority', limit=null (all eligible)
 *
 * `priority` is the primary default because:
 *   - it's what the engine already uses for tie-breaking, so admins
 *     are familiar
 *   - stable across page reloads (no random shuffling)
 *   - direct admin control: bump priority to feature a promo
 *
 * `rotate` and `random` exist for tenants who want broader exposure
 * across multiple same-priority promos:
 *   - `rotate`: deterministic rotation by hour bucket (stable across
 *     a TTL window, then advances). Customers see different promos
 *     on revisit but not on every render.
 *   - `random`: pure shuffle per call. Useful for A/B-testing
 *     creative within a tag without pinning priorities.
 */

export type WidgetSelectionRankBy =
	| 'priority'
	| 'newest'
	| 'ending-soonest'
	| 'rotate'
	| 'random';

export interface WidgetSelectionConfig {
	mode: 'auto' | 'manual';
	auto?: {
		rankBy?: WidgetSelectionRankBy;
		/** null / omitted = take all eligible. */
		limit?: number | null;
		/**
		 * Closed-enum tags (`PromoDisplayLocation[]`) — pre-filter step
		 * before rank. When omitted or empty, all eligible candidates
		 * pass through. When set, only candidates whose own
		 * `displayLocations` overlaps any value in the filter pass.
		 *
		 * Promos with empty `displayLocations` (the default for
		 * un-tagged promos) are EXCLUDED whenever this filter is set
		 * — we want untagged promos to surface only in widgets that
		 * haven't asked for a specific location. Set the filter empty
		 * to revert to legacy "take all" behaviour.
		 */
		displayLocations?: Array<string>;
	};
	manual?: {
		promoIds: Array<number>;
	};
	/**
	 * Optional clock seed for `rotate` rank. Tests inject a fixed
	 * timestamp for determinism; production callers leave it
	 * unset and the helper uses `Date.now()`.
	 */
	nowMs?: number;
}

/**
 * Apply a selection config to a set of eligible promo candidates.
 * Returns the slice of candidates the widget should render. Stable
 * (deterministic) for `priority` / `newest` / `ending-soonest` —
 * customers refreshing the homepage shouldn't see those widgets
 * shuffle. `rotate` is deterministic within a 1-hour window.
 * `random` is non-deterministic by design.
 */
export function selectPromosForWidget(
	candidates: Array<PromoCandidate>,
	config: WidgetSelectionConfig,
): Array<PromoCandidate> {
	if (config.mode === 'manual') {
		const ids = config.manual?.promoIds ?? [];
		if (ids.length === 0) {
			return [];
		}
		// Preserve admin-supplied order; drop any IDs that aren't in
		// the eligible set so an expired pinned promo doesn't render.
		const byId = new Map(candidates.map((c) => [c.id, c]));
		return ids
			.map((id) => byId.get(id))
			.filter((c): c is PromoCandidate => c != null);
	}

	// Pre-filter: keep only candidates whose displayLocations
	// overlap the requested set. See WidgetSelectionConfig comment
	// for why untagged promos are excluded when a filter is set.
	const locationFilter = config.auto?.displayLocations ?? [];
	const filtered =
		locationFilter.length === 0
			? candidates
			: candidates.filter((c) => {
					const locs = c.displayLocations ?? [];
					if (locs.length === 0) return false;
					return locs.some((l) => locationFilter.includes(l));
				});

	const rankBy: WidgetSelectionRankBy = config.auto?.rankBy ?? 'priority';
	const sorted = applyRank(filtered, rankBy, config.nowMs);

	const limit = config.auto?.limit ?? null;
	if (limit == null || limit <= 0 || sorted.length <= limit) {
		return sorted;
	}
	return sorted.slice(0, limit);
}

/**
 * Apply the requested rank to the filtered candidate set. Pulled
 * out of `selectPromosForWidget` so the rotate/random branches can
 * fall through cleanly without bloating the comparator.
 */
function applyRank(
	candidates: Array<PromoCandidate>,
	rankBy: WidgetSelectionRankBy,
	nowMs?: number,
): Array<PromoCandidate> {
	if (candidates.length <= 1) return candidates;
	if (rankBy === 'rotate') {
		return rotateByHourBucket(candidates, nowMs);
	}
	if (rankBy === 'random') {
		return shuffleInPlace([...candidates]);
	}
	return [...candidates].sort((a, b) => compareForRank(a, b, rankBy));
}

/**
 * Rotates the candidate list by a stable hour-bucket key. Within a
 * given hour, every render returns the same order (cache-friendly).
 * On the next hour the rotation advances by one slot, so a customer
 * revisiting later sees a different promo at the top without the
 * order changing mid-session.
 *
 * We sort first by priority+id (the same stable order
 * `priority` would produce) so the rotation is over a deterministic
 * sequence — admins can predict "if I have 3 promos at priority 100,
 * each one gets ~1 in 3 hours of top placement".
 */
function rotateByHourBucket(
	candidates: Array<PromoCandidate>,
	nowMs?: number,
): Array<PromoCandidate> {
	const stable = [...candidates].sort((a, b) =>
		compareForRank(a, b, 'priority'),
	);
	const t = nowMs ?? Date.now();
	const bucket = Math.floor(t / 3_600_000);
	const offset = bucket % stable.length;
	return [...stable.slice(offset), ...stable.slice(0, offset)];
}

/**
 * Fisher-Yates shuffle using `Math.random()`. Not cryptographically
 * secure — fine for marketing display fairness, NOT for anything
 * security-sensitive (we don't use this for promo eligibility).
 */
function shuffleInPlace<T>(arr: Array<T>): Array<T> {
	for (let i = arr.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[arr[i], arr[j]] = [arr[j], arr[i]];
	}
	return arr;
}

function compareForRank(
	a: PromoCandidate,
	b: PromoCandidate,
	rankBy: 'priority' | 'newest' | 'ending-soonest',
): number {
	switch (rankBy) {
		case 'priority': {
			// Higher priority first.
			if (b.priority !== a.priority) return b.priority - a.priority;
			// Tiebreak by newest start date so newly-launched promos
			// surface ahead of older same-priority ones.
			return startMillis(b) - startMillis(a);
		}
		case 'newest': {
			// Most-recently-started first.
			const diff = startMillis(b) - startMillis(a);
			if (diff !== 0) return diff;
			// Tiebreak by priority desc.
			return b.priority - a.priority;
		}
		case 'ending-soonest': {
			// Soonest end first — gives admins a "clearance bin"
			// pattern (last chance to redeem).
			const diff = endMillis(a) - endMillis(b);
			if (diff !== 0) return diff;
			return b.priority - a.priority;
		}
		default:
			return 0;
	}
}

function startMillis(p: PromoCandidate): number {
	const t = new Date(p.effectiveStartDate).getTime();
	return Number.isNaN(t) ? 0 : t;
}

function endMillis(p: PromoCandidate): number {
	const t = new Date(p.effectiveEndDate).getTime();
	return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}
